import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TextFieldLabel from "./TextFieldLabel";
import {
  CustomTextField,
  CustomTextFieldDisabled,
} from "assets/styles/globalStyledComponent";
import { IndianCurrency } from "app/utils/helper";

const MODES = [
  { key: "CashMode", label: "Cash" },
  { key: "UpiMode", label: "UPI" },
  { key: "CardMode", label: "Card" },
  { key: "NEFTMode", label: "NEFT" },
  { key: "ChequeMode", label: "Cheque" },
];

function buildPaymentDetails(v) {
  const enabled = v?.enabled ?? {};
  const amount = v?.amount ?? {};

  const out = {};
  if (enabled.CashMode)
    out.CashMode = { amount: Number(amount.CashMode ?? 0) };
  if (enabled.UpiMode)
    out.UpiMode = {
      UPI_platform: v?.upi?.UPI_platform ?? "--",
      upiNumber: v?.upi?.upiNumber ?? "--",
      transactionId: v?.upi?.transactionId ?? "--",
      amount: Number(amount.UpiMode ?? 0),
    };
  if (enabled.CardMode)
    out.CardMode = {
      cardHolderName: v?.card?.cardHolderName ?? "",
      cardNumber: v?.card?.cardNumber ?? "",
      approvalCode: v?.card?.approvalCode ?? "",
      amount: Number(amount.CardMode ?? 0),
    };
  if (enabled.NEFTMode)
    out.NEFTMode = {
      referenceNumber: v?.neft?.referenceNumber ?? "--",
      transactionNumber: v?.neft?.transactionNumber ?? "--",
      amount: Number(amount.NEFTMode ?? 0),
    };
  if (enabled.ChequeMode)
    out.ChequeMode = {
      chequeNumber: v?.cheque?.chequeNumber ?? "--",
      depositionDate: v?.cheque?.depositionDate ?? new Date(),
      amount: Number(amount.ChequeMode ?? 0),
    };

  return out;
}

const PaymentModeComponent2 = ({
  amount = 0,
  enforceTotal = true,
  onChange = () => {},
  showErrors = false,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [activeMode, setActiveMode] = useState("CashMode");

  const schema = useMemo(
    () =>
      yup
        .object({
          enabled: yup.object({
            CashMode: yup.boolean().default(true),
            UpiMode: yup.boolean().default(false),
            CardMode: yup.boolean().default(false),
            NEFTMode: yup.boolean().default(false),
            ChequeMode: yup.boolean().default(false),
          }),
          amount: yup.object({
            CashMode: yup.number().typeError("Amount required").min(0).required(),
            UpiMode: yup.number().typeError("Amount required").min(0).required(),
            CardMode: yup.number().typeError("Amount required").min(0).required(),
            NEFTMode: yup.number().typeError("Amount required").min(0).required(),
            ChequeMode: yup
              .number()
              .typeError("Amount required")
              .min(0)
              .required(),
          }),
          upi: yup.object({
            UPI_platform: yup.string().when("$enabledUpi", {
              is: true,
              then: (s) => s.required("Platform required"),
              otherwise: (s) => s.notRequired(),
            }),
            upiNumber: yup.string().when("$enabledUpi", {
              is: true,
              then: (s) => s.required("UPI Number required"),
              otherwise: (s) => s.notRequired(),
            }),
            transactionId: yup.string().when("$enabledUpi", {
              is: true,
              then: (s) => s.required("Transaction id required"),
              otherwise: (s) => s.notRequired(),
            }),
          }),
          card: yup.object({
            cardHolderName: yup.string().when("$enabledCard", {
              is: true,
              then: (s) => s.required("Card holder name required"),
              otherwise: (s) => s.notRequired(),
            }),
            cardNumber: yup.string().when("$enabledCard", {
              is: true,
              then: (s) => s.required("Card number required"),
              otherwise: (s) => s.notRequired(),
            }),
            approvalCode: yup.string().when("$enabledCard", {
              is: true,
              then: (s) => s.required("Approval code required"),
              otherwise: (s) => s.notRequired(),
            }),
          }),
          neft: yup.object({
            referenceNumber: yup.string().when("$enabledNeft", {
              is: true,
              then: (s) => s.required("Reference number required"),
              otherwise: (s) => s.notRequired(),
            }),
            transactionNumber: yup.string().when("$enabledNeft", {
              is: true,
              then: (s) => s.required("Transaction number required"),
              otherwise: (s) => s.notRequired(),
            }),
          }),
          cheque: yup.object({
            chequeNumber: yup.string().when("$enabledCheque", {
              is: true,
              then: (s) => s.required("Cheque number required"),
              otherwise: (s) => s.notRequired(),
            }),
            depositionDate: yup.mixed().when("$enabledCheque", {
              is: true,
              then: (s) => s.required("Deposition date required"),
              otherwise: (s) => s.notRequired(),
            }),
          }),
        })
        .test("at-least-one", "Select at least one payment mode", (v) =>
          Object.values(v?.enabled ?? {}).some(Boolean),
        )
        .test("selected-amount", "Enter amount for selected modes", (v) => {
          // Don't force positive amounts until total amount is > 0
          if (Number(v?.__totalAmount ?? 0) <= 0) return true;
          const enabled = v?.enabled ?? {};
          const amount = v?.amount ?? {};
          const selected = Object.keys(enabled).filter((k) => enabled[k]);
          if (selected.length === 0) return true;
          return selected.every((k) => Number(amount?.[k] ?? 0) > 0);
        })
        .test("sum-match", "Selected amounts must match total", (v) => {
          if (Number(v?.__totalAmount ?? 0) <= 0) return true;
          if (!enforceTotal) return true;
          const enabled = v?.enabled ?? {};
          const amountObj = v?.amount ?? {};
          const sum = Object.keys(enabled)
            .filter((k) => enabled[k])
            .reduce((acc, k) => acc + Number(amountObj?.[k] ?? 0), 0);
          return Number(sum) === Number(v?.__totalAmount ?? 0);
        }),
    [enforceTotal],
  );

  const defaultValues = useMemo(
    () => ({
      __totalAmount: Number(amount ?? 0),
      enabled: {
        CashMode: true,
        UpiMode: false,
        CardMode: false,
        NEFTMode: false,
        ChequeMode: false,
      },
      amount: {
        CashMode: Number(amount ?? 0),
        UpiMode: 0,
        CardMode: 0,
        NEFTMode: 0,
        ChequeMode: 0,
      },
      upi: { UPI_platform: "", upiNumber: "", transactionId: "" },
      card: { cardHolderName: "", cardNumber: "", approvalCode: "" },
      neft: { referenceNumber: "", transactionNumber: "" },
      cheque: { chequeNumber: "", depositionDate: new Date() },
    }),
    [amount],
  );

  const {
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema, {
      context: {
        enabledUpi: defaultValues.enabled.UpiMode,
        enabledCard: defaultValues.enabled.CardMode,
        enabledNeft: defaultValues.enabled.NEFTMode,
        enabledCheque: defaultValues.enabled.ChequeMode,
      },
    }),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const watched = useWatch({ control });
  const enabled = useMemo(() => watched?.enabled ?? {}, [watched?.enabled]);
  const amountObj = useMemo(() => watched?.amount ?? {}, [watched?.amount]);

  const totalSelectedAmount = useMemo(() => {
    const selected = Object.keys(enabled).filter((k) => enabled[k]);
    return selected.reduce((acc, k) => acc + Number(amountObj?.[k] ?? 0), 0);
  }, [enabled, amountObj]);

  // When toggles change, revalidate and optionally adjust cash remainder
  useEffect(() => {
    if (enforceTotal && enabled.CashMode) {
      const selected = Object.keys(enabled).filter((k) => enabled[k]);
      const nonCash = selected
        .filter((k) => k !== "CashMode")
        .reduce((acc, k) => acc + Number(amountObj?.[k] ?? 0), 0);
      const remainder = Math.max(0, Number(amount) - nonCash);
      if (Number(amountObj?.CashMode ?? 0) !== Number(remainder)) {
        setValue("amount.CashMode", remainder, { shouldValidate: true });
      }
    }
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled.CashMode,
    enabled.UpiMode,
    enabled.CardMode,
    enabled.NEFTMode,
    enabled.ChequeMode,
  ]);

  // Emit value up to parent (null when invalid)
  useEffect(() => {
    const payload = isValid ? buildPaymentDetails(getValues()) : null;
    onChange(payload, { isValid, totalSelectedAmount, errors });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, isValid, totalSelectedAmount]);

  const toggleMode = (modeKey) => {
    const next = !Boolean(enabled?.[modeKey]);
    setValue(`enabled.${modeKey}`, next, { shouldValidate: true });
    if (!next) setValue(`amount.${modeKey}`, 0, { shouldValidate: true });

    // keep at least one selected (Cash default)
    const after = { ...(enabled ?? {}), [modeKey]: next };
    const any = Object.values(after).some(Boolean);
    if (!any) {
      setValue("enabled.CashMode", true, { shouldValidate: true });
      setActiveMode("CashMode");
    } else if (next) {
      setActiveMode(modeKey);
    } else if (activeMode === modeKey) {
      const nextActive =
        Object.keys(after).find((k) => after[k]) ?? "CashMode";
      setActiveMode(nextActive);
    }

    if (showErrors) clearErrors();
  };

  const amountMismatch =
    enforceTotal && Number(totalSelectedAmount) !== Number(amount);

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>Payment Mode</Typography>

      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        {MODES.map((m) => {
          const selected = Boolean(enabled?.[m.key]);
          const isActive = activeMode === m.key;
          const modeHasError =
            showErrors &&
            (m.key === "UpiMode"
              ? Boolean(errors?.upi) || Boolean(errors?.amount?.UpiMode)
              : m.key === "CardMode"
              ? Boolean(errors?.card) || Boolean(errors?.amount?.CardMode)
              : m.key === "NEFTMode"
              ? Boolean(errors?.neft) || Boolean(errors?.amount?.NEFTMode)
              : m.key === "ChequeMode"
              ? Boolean(errors?.cheque) || Boolean(errors?.amount?.ChequeMode)
              : Boolean(errors?.amount?.CashMode) || Boolean(errors?.root));

          return (
            <Grid item xs={6} md={2.4} key={m.key}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: modeHasError
                    ? "error.main"
                    : selected
                    ? "secondary.main"
                    : "divider",
                  borderWidth: selected || modeHasError ? 2 : 1,
                  position: "relative",
                  boxShadow: isActive ? "0 0 0 2px rgba(0,0,0,0.06)" : "none",
                }}
              >
                <CardActionArea
                  onClick={() => toggleMode(m.key)}
                  sx={{ height: "100%" }}
                >
                  <CardContent sx={{ py: 1.25, px: 1.25 }}>
                    <Typography sx={{ fontWeight: 700, textAlign: "center" }}>
                      {m.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, opacity: 0.7, textAlign: "center" }}
                    >
                      {selected ? "Selected" : "Tap to select"}
                    </Typography>
                  </CardContent>
                </CardActionArea>

                {selected && (
                  <CheckCircleIcon
                    color="secondary"
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: 6,
                      fontSize: 18,
                      background: "#fff",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextFieldLabel title="Total Amount" />
          <CustomTextFieldDisabled size="small" value={IndianCurrency(amount)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextFieldLabel title="Selected Total" />
          <CustomTextFieldDisabled
            size="small"
            value={IndianCurrency(Number(totalSelectedAmount))}
            sx={{
              "& input": { color: amountMismatch ? "red" : "inherit" },
            }}
          />
          {amountMismatch && (
            <Typography sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
              Selected amounts must equal {IndianCurrency(Number(amount))}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {enabled.CashMode && activeMode === "CashMode" && (
          <Grid item xs={12} md={4}>
            <TextFieldLabel title="Cash Amount" />
            <Controller
              name="amount.CashMode"
              control={control}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  size="small"
                  type="number"
                  fullWidth
                  disabled={enforceTotal}
                  error={showErrors && Boolean(errors?.amount?.CashMode)}
                  helperText={
                    showErrors ? errors?.amount?.CashMode?.message : undefined
                  }
                />
              )}
            />
          </Grid>
        )}

        {enabled.UpiMode && activeMode === "UpiMode" && (
          <>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="UPI Amount" />
              <Controller
                name="amount.UpiMode"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    type="number"
                    fullWidth
                    error={showErrors && Boolean(errors?.amount?.UpiMode)}
                    helperText={
                      showErrors ? errors?.amount?.UpiMode?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="UPI Platform" />
              <Controller
                name="upi.UPI_platform"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.upi?.UPI_platform)}
                    helperText={
                      showErrors ? errors?.upi?.UPI_platform?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="UPI Number" />
              <Controller
                name="upi.upiNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.upi?.upiNumber)}
                    helperText={
                      showErrors ? errors?.upi?.upiNumber?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Transaction ID" />
              <Controller
                name="upi.transactionId"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.upi?.transactionId)}
                    helperText={
                      showErrors
                        ? errors?.upi?.transactionId?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
          </>
        )}

        {enabled.CardMode && activeMode === "CardMode" && (
          <>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Card Amount" />
              <Controller
                name="amount.CardMode"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    type="number"
                    fullWidth
                    error={showErrors && Boolean(errors?.amount?.CardMode)}
                    helperText={
                      showErrors ? errors?.amount?.CardMode?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Card Holder Name" />
              <Controller
                name="card.cardHolderName"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.card?.cardHolderName)}
                    helperText={
                      showErrors
                        ? errors?.card?.cardHolderName?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Card Number" />
              <Controller
                name="card.cardNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.card?.cardNumber)}
                    helperText={
                      showErrors ? errors?.card?.cardNumber?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Approval Code" />
              <Controller
                name="card.approvalCode"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.card?.approvalCode)}
                    helperText={
                      showErrors
                        ? errors?.card?.approvalCode?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
          </>
        )}

        {enabled.NEFTMode && activeMode === "NEFTMode" && (
          <>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="NEFT Amount" />
              <Controller
                name="amount.NEFTMode"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    type="number"
                    fullWidth
                    error={showErrors && Boolean(errors?.amount?.NEFTMode)}
                    helperText={
                      showErrors ? errors?.amount?.NEFTMode?.message : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Reference Number" />
              <Controller
                name="neft.referenceNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.neft?.referenceNumber)}
                    helperText={
                      showErrors
                        ? errors?.neft?.referenceNumber?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Transaction Number" />
              <Controller
                name="neft.transactionNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={
                      showErrors && Boolean(errors?.neft?.transactionNumber)
                    }
                    helperText={
                      showErrors
                        ? errors?.neft?.transactionNumber?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
          </>
        )}

        {enabled.ChequeMode && activeMode === "ChequeMode" && (
          <>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Cheque Amount" />
              <Controller
                name="amount.ChequeMode"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    type="number"
                    fullWidth
                    error={showErrors && Boolean(errors?.amount?.ChequeMode)}
                    helperText={
                      showErrors
                        ? errors?.amount?.ChequeMode?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Cheque Number" />
              <Controller
                name="cheque.chequeNumber"
                control={control}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={showErrors && Boolean(errors?.cheque?.chequeNumber)}
                    helperText={
                      showErrors
                        ? errors?.cheque?.chequeNumber?.message
                        : undefined
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldLabel title="Deposition Date" />
              <Controller
                name="cheque.depositionDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    inputFormat="DD-MM-YYYY"
                    value={field.value}
                    onChange={(e) => field.onChange(e)}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        size="small"
                        sx={{ width: isMobile ? "auto" : "17rem" }}
                        error={
                          showErrors && Boolean(errors?.cheque?.depositionDate)
                        }
                        helperText={
                          showErrors
                            ? errors?.cheque?.depositionDate?.message
                            : undefined
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>

      {showErrors && !isValid && (
        <Typography sx={{ color: "red", fontSize: 12, mt: 1 }}>
          Please fix the highlighted payment mode fields.
        </Typography>
      )}
    </Box>
  );
};

export default PaymentModeComponent2;

